import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuction } from '../../context/AuctionContext';
import { collection, doc, onSnapshot, query, writeBatch, arrayUnion, updateDoc, orderBy, serverTimestamp, deleteDoc, deleteField, runTransaction } from 'firebase/firestore';
import { db, storage, functions } from '../../firebase'; // Import storage and functions
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Import storage functions
import {
    Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, FormControlLabel, Checkbox, Switch
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as XLSX from 'xlsx';
import { table, avatarSizes, page } from '../../theme/commonStyles';
import { seasonPlayersCollection } from '../../services/players';
import { fetchSeasons } from '../../services/seasons';

// --- Team Validation Constants ---
const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

const TEAM_RULES = {
    'Totaal': { min: 12, max: 12 },
    'Coach': { min: 1, max: 1 },
    'Keepers': { min: 1, max: 1 },
    'Verdedigers': { min: 3, max: 5 },
    'Middenvelders': { min: 3, max: 5 },
    'Aanvallers': { min: 2, max: 4 },
};

const getRuleKeyForPosition = (dutchPosition) => {
    if (dutchPosition === 'Coach') return 'Coach';
    if (dutchPosition === 'Keeper') return 'Keepers';
    if (dutchPosition) return dutchPosition + 's';
    return null;
}

const validateTeamComposition = (teamPlayers, playerToAdd) => {
    const newTeamSize = teamPlayers.length + 1;
    if (newTeamSize > TEAM_RULES['Totaal'].max) {
        return `Het team heeft al het maximum aantal spelers (${TEAM_RULES['Totaal'].max}).`;
    }

    const composition = teamPlayers.reduce((acc, player) => {
        const dutchPosition = POSITION_MAP[player.position] || player.position;
        acc[dutchPosition] = (acc[dutchPosition] || 0) + 1;
        return acc;
    }, {});

    const newPlayerDutchPosition = POSITION_MAP[playerToAdd.position] || playerToAdd.position;
    const ruleKeyForNewPlayer = getRuleKeyForPosition(newPlayerDutchPosition);

    if (ruleKeyForNewPlayer && TEAM_RULES[ruleKeyForNewPlayer]) {
        const currentCount = composition[newPlayerDutchPosition] || 0;
        const maxCount = TEAM_RULES[ruleKeyForNewPlayer].max;
        if (currentCount >= maxCount) {
            return `Het team heeft al het maximum aantal ${ruleKeyForNewPlayer.toLowerCase()} (${maxCount}).`;
        }
    }

    const compositionAfterAdding = { ...composition };
    compositionAfterAdding[newPlayerDutchPosition] = (compositionAfterAdding[newPlayerDutchPosition] || 0) + 1;

    const remainingSlots = TEAM_RULES['Totaal'].max - newTeamSize;
    let stillNeeded = 0;
    for (const ruleKey in TEAM_RULES) {
        if (ruleKey === 'Totaal') continue;

        const positionName = ruleKey === 'Coach' ? 'Coach' : (ruleKey === 'Keepers' ? 'Keeper' : ruleKey.slice(0, -1));
        const min = TEAM_RULES[ruleKey].min;
        const current = compositionAfterAdding[positionName] || 0;
        
        if (current < min) {
            stillNeeded += (min - current);
        }
    }

    if (stillNeeded > remainingSlots) {
        return `Deze speler kan niet worden toegevoegd, omdat het dan onmogelijk wordt om aan de minimale teamvereisten voor andere posities te voldoen.`;
    }

    return null; // Validation passes
};

// Helper function to export data to Excel
const exportToExcel = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
};


// Helper component for sortable rows
function SortablePlayerRow({ player, handleClickOpen, handleOpenConfirmDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: player.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 10 : 'auto',
        backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
    };

    return (
        <TableRow ref={setNodeRef} style={style} {...attributes} hover>
            <TableCell sx={table.dragCell} {...listeners}>
                <DragIndicatorIcon />
            </TableCell>
            <TableCell onClick={() => handleClickOpen(player)} sx={table.clickableCell}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={player.photoUrl} alt={player.name} sx={avatarSizes.md} />
                    {player.name}
                </Box>
            </TableCell>
            <TableCell onClick={() => handleClickOpen(player)} sx={table.clickableCell}>{player.position}</TableCell>
            <TableCell onClick={() => handleClickOpen(player)} sx={table.clickableCell}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={player.clubLogo} alt={player.clubName} sx={avatarSizes.sm} />
                    {player.clubName}
                </Box>
            </TableCell>
            <TableCell align="center" onClick={() => handleClickOpen(player)} sx={table.clickableCell}>
                {player.auctionText && <CheckCircleIcon color="success" fontSize="small" />}
            </TableCell>
            <TableCell align="right" sx={table.narrowColumn}>
                <IconButton
                    aria-label="delete player"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleOpenConfirmDelete(player); }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

function AdminAuction() {
    const { auctionId } = useAuction();
    const [auction, setAuction] = useState(null);
    const [auctionStarted, setAuctionStarted] = useState(false);
    const [bidder, setBidder] = useState('');
    const [amount, setAmount] = useState('');
    const [participants, setParticipants] = useState([]);
    const [playersForAuction, setPlayersForAuction] = useState([]);
    const [allSeasonPlayers, setAllSeasonPlayers] = useState([]);
    const [auctionTransfers, setAuctionTransfers] = useState([]);
    const [playerStats, setPlayerStats] = useState({});
    const [prevSeasonId, setPrevSeasonId] = useState(null);
    const [prevSeasonTotals, setPrevSeasonTotals] = useState({});
    // De speler waarvan nu de kaart (niet de spelersvrouw) in de projectie-popup staat,
    // zodat we die opnieuw kunnen tekenen zodra async data (stats/totals) binnenkomt.
    const shownCardPlayerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [auctionText, setAuctionText] = useState('');
    const [confirmDeleteTransferOpen, setConfirmDeleteTransferOpen] = useState(false);
    const [transferToDelete, setTransferToDelete] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [playerToDelete, setPlayerToDelete] = useState(null);
    const [confirmRevertBlindPoolOpen, setConfirmRevertBlindPoolOpen] = useState(false);
    const [playerToRevert, setPlayerToRevert] = useState(null);
    const [decoupledControl, setDecoupledControl] = useState(false);
    const [decoupledPlayerId, setDecoupledPlayerId] = useState(null);
    const [isShowingWifeForDecoupled, setIsShowingWifeForDecoupled] = useState(false);
    const [decoupledPlayerList, setDecoupledPlayerList] = useState([]); // For stable decoupled navigation
    const [editingTransferId, setEditingTransferId] = useState(null);
    const [editingAmount, setEditingAmount] = useState('');
    const [allAuctionedOpen, setAllAuctionedOpen] = useState(false);
    const [validationErrorOpen, setValidationErrorOpen] = useState(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const [confirmShuffleOpen, setConfirmShuffleOpen] = useState(false);

    // New state for "spelersvrouw" feature
    const [showWifeImage, setShowWifeImage] = useState(false);
    const [wifeImageFile, setWifeImageFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [playerJustSold, setPlayerJustSold] = useState(null);


    const participantsMap = useMemo(() => {
        const map = new Map();
        participants.forEach(p => {
            map.set(p.uid, p.name);
        });
        return map;
    }, [participants]);

    const allPlayersMap = useMemo(() => {
        const map = new Map();
        allSeasonPlayers.forEach(p => map.set(String(p.id), p));
        playersForAuction.forEach(p => {
            if (!map.has(String(p.id))) {
                map.set(String(p.id), p);
            }
        });
        return map;
    }, [allSeasonPlayers, playersForAuction]);

    const auctionedOffPlayers = useMemo(() => {
        return auctionTransfers.filter(t => t.type === 'auction');
    }, [auctionTransfers]);

    const blindPoolPlayers = useMemo(() => {
        const transferedPlayerIds = new Set(auctionTransfers.map(t => String(t.playerId)));
        const players = playersForAuction
            .filter(p => p.auctioned === 1 && !transferedPlayerIds.has(String(p.id)))
            .map(p => {
                const globalPlayer = allPlayersMap.get(String(p.id));
                const position = globalPlayer?.position || p.position;
                return {
                    ...p,
                    position: POSITION_MAP[position] || position
                }
            });
        players.sort((a, b) => (b.auctionedTimestamp?.seconds || 0) - (a.auctionedTimestamp?.seconds || 0));
        return players;

    }, [playersForAuction, auctionTransfers, allPlayersMap]);

    useEffect(() => {
        if (!auctionId) return;

        const unsubAuction = onSnapshot(doc(db, "auctions", auctionId), (docSnap) => {
            if (docSnap.exists()) {
                const auctionData = docSnap.data();
                setAuction(auctionData);
                setAuctionStarted(auctionData.auctionStarted || false);
                setDecoupledControl(auctionData.decoupledControl || false);
            } else {
                setError("Veiling niet gevonden.");
            }
            setLoading(false);
        }, (err) => {
            setError("Fout bij het ophalen van veilingdata.");
            console.error(err);
            setLoading(false);
        });

        const participantsQuery = query(collection(db, 'auctions', auctionId, 'participants'));
        const unsubParticipants = onSnapshot(participantsQuery, (snapshot) => {
            const fetchedParticipants = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            fetchedParticipants.sort((a, b) => {
                const nameA = a.name || a.email || '';
                const nameB = b.name || b.email || '';
                return nameA.localeCompare(nameB);
            });
            setParticipants(fetchedParticipants);
        });

        const playersQuery = query(collection(db, 'auctions', auctionId, 'auctionPlayers'), orderBy('auctionOrder'));
        const unsubPlayers = onSnapshot(playersQuery, (snapshot) => {
            const fetchedPlayers = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setPlayersForAuction(fetchedPlayers);
        });

        const unsubTransfers = onSnapshot(query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'desc')), (snapshot) => {
            const fetchedTransfers = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setAuctionTransfers(fetchedTransfers);
        });

        return () => {
            unsubAuction();
            unsubPlayers();
            unsubParticipants();
            unsubTransfers();
        };
    }, [auctionId]);

    useEffect(() => {
        if (!auction?.season) return;
        const unsub = onSnapshot(seasonPlayersCollection(auction.season), (snapshot) => {
            const fetched = snapshot.docs.map(doc => doc.data());
            setAllSeasonPlayers(fetched);
        });
        return () => unsub();
    }, [auction]);

    useEffect(() => {
        if (playersForAuction.length === 0) return;

        const fetchPlayerStats = async () => {
            try {
                const playerIds = playersForAuction.map(p => String(p.id));
                const getPlayerStats = httpsCallable(functions, 'getPlayerStats');
                const result = await getPlayerStats({ playerIds, seasonId: auction.season });
                setPlayerStats(result.data);
            } catch (err) {
                console.error("Error fetching player stats for auction:", err);
            }
        };

        fetchPlayerStats();
    }, [playersForAuction, auction?.season]);

    // Het vorige seizoen bepalen: het meest recent afgeronde seizoen. De badge
    // "Punten vorig seizoen" op de projectiekaart komt uit dat seizoen zijn totals.
    useEffect(() => {
        const bepaalVorigSeizoen = async () => {
            try {
                const seasons = await fetchSeasons(); // nieuwste eerst
                const vorige = seasons.find(s => s.status === 'afgerond');
                setPrevSeasonId(vorige ? String(vorige.id) : null);
            } catch (err) {
                console.error("Kon het vorige seizoen niet bepalen:", err);
                setPrevSeasonId(null);
            }
        };

        bepaalVorigSeizoen();
    }, []);

    // De totalen van het vorige seizoen ophalen voor de spelers in deze veiling.
    useEffect(() => {
        if (!prevSeasonId || playersForAuction.length === 0) {
            setPrevSeasonTotals({});
            return;
        }

        const fetchPrevSeasonTotals = async () => {
            try {
                const playerIds = playersForAuction.map(p => String(p.id));
                const getSeasonPlayerTotals = httpsCallable(functions, 'getSeasonPlayerTotals');
                const result = await getSeasonPlayerTotals({ playerIds, seasonId: prevSeasonId });
                setPrevSeasonTotals(result.data);
            } catch (err) {
                console.error("Error fetching previous season totals for auction:", err);
            }
        };

        fetchPrevSeasonTotals();
    }, [playersForAuction, prevSeasonId]);

    const playersToAuction = useMemo(() => playersForAuction.filter(p => !p.auctioned), [playersForAuction]);

    const enrichedPlayersToAuction = useMemo(() => {
        return playersToAuction.map(auctionPlayer => {
            const globalPlayer = allPlayersMap.get(String(auctionPlayer.id));
            const position = globalPlayer?.position || auctionPlayer.position;
            return {
                ...auctionPlayer,
                photoUrl: globalPlayer?.photoUrl || auctionPlayer.photoUrl,
                clubLogo: globalPlayer?.clubLogo || auctionPlayer.clubLogo,
                clubName: globalPlayer?.clubName || auctionPlayer.clubName,
                position: POSITION_MAP[position] || position,
            };
        });
    }, [playersToAuction, allPlayersMap]);

    useEffect(() => {
        if (decoupledControl && enrichedPlayersToAuction.length > 0 && decoupledPlayerList.length === 0) {
            setDecoupledPlayerList(enrichedPlayersToAuction);
            
            if (!decoupledPlayerId && enrichedPlayersToAuction.length > 0) {
                const firstPlayer = enrichedPlayersToAuction[0];
                setDecoupledPlayerId(firstPlayer.id);
                setIsShowingWifeForDecoupled(!!firstPlayer.wifeImageUrl);
            }
        }
    }, [decoupledControl, enrichedPlayersToAuction, decoupledPlayerList, decoupledPlayerId]);

    const currentPlayer = enrichedPlayersToAuction[0];

    const handleToggleDecoupledControl = async (event) => {
        const isEnabled = event.target.checked;
        const auctionRef = doc(db, 'auctions', auctionId);
        await updateDoc(auctionRef, { decoupledControl: isEnabled });
    
        if (isEnabled) {
            setDecoupledPlayerList(enrichedPlayersToAuction);
            const firstPlayer = enrichedPlayersToAuction[0];
            if (firstPlayer) {
                setDecoupledPlayerId(firstPlayer.id);
                const showWife = !!firstPlayer.wifeImageUrl;
                setIsShowingWifeForDecoupled(showWife);
            } else {
                setDecoupledPlayerId(null);
            }
        } else {
            setDecoupledPlayerId(null);
            setDecoupledPlayerList([]);
        }
    };

    const getPlayerCardHtml = useCallback((player, stats, backgroundUrl, prevSeasonPoints, scoutCount) => {
        if (!player) return '';

        const bodyStyle = backgroundUrl
            ? `background-image: url('${backgroundUrl}'); background-size: cover; background-position: center; background-repeat: no-repeat;`
            : 'background-color: #000139;';

        const manualStatsHtml = (stats && (stats.matches || stats.goals || stats.assists)) ? `
            <div class="stat-item"><span>${stats.matches || 0}</span><span>Wedstrijden</span></div>
            <div class="stat-item"><span>${stats.goals || 0}</span><span>Goals</span></div>
            <div class="stat-item"><span>${stats.assists || 0}</span><span>Assists</span></div>
        ` : '';

        // Totaal aantal punten uit het vorige seizoen (opgeslagen via "Seizoenstotalen
        // opslaan" op de stamgegevens, uit playerScores/{vorigSeizoen}/totals). Als apart
        // stat-item naast de assists, met extra ruimte ervoor zodat het los staat.
        const seasonStatHtml = (typeof prevSeasonPoints === 'number' && prevSeasonPoints > 0) ? `
            <div class="stat-item stat-item-season"><span>${prevSeasonPoints}</span><span>Vorig seizoen</span></div>
        ` : '';

        const statsHtml = (manualStatsHtml || seasonStatHtml)
            ? `<div class="player-stats">${manualStatsHtml}${seasonStatHtml}</div>`
            : '';

        // Hoe vaak de speler door deelnemers gescout is.
        const scoutHtml = (typeof scoutCount === 'number' && scoutCount > 0) ? `
            <div class="pill pill-scout">
                <span class="pill-star">&#9733;</span>
                <span class="pill-value">${scoutCount}&times;</span>
                <span class="pill-tag">gescout</span>
            </div>
        ` : '';

        const badgesHtml = scoutHtml ? `<div class="badges">${scoutHtml}</div>` : '';

        return `
            <style>
                body { 
                    ${bodyStyle}
                    color: #ffffff; 
                    font-family: "Roboto", "Helvetica", "Arial", sans-serif; 
                    margin: 0; 
                    padding: 40px; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    height: 100vh; 
                    box-sizing: border-box; 
                }
                .card { background-color: rgba(0, 0, 0, 0.65); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; backdrop-filter: blur(10px); padding: 40px; display: flex; gap: 40px; align-items: center; max-width: 900px; width: 100%; box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37); }
                .player-image { width: 300px; height: 300px; object-fit: cover; border-radius: 50%; border: 4px solid #fecb00; }
                .player-info { flex-grow: 1; }
                .player-name { font-size: 4.5em; font-weight: 700; margin: 0; line-height: 1.1; text-shadow: 2px 2px 8px rgba(0,0,0,0.7); }
                .player-club { font-size: 2.5em; color: #a9b4c8; margin: 10px 0; display: flex; align-items: center; gap: 15px; text-shadow: 1px 1px 4px rgba(0,0,0,0.5); }
                .club-logo { height: 50px; width: 50px; object-fit: contain; }
                .player-details { font-size: 1.8em; color: #a9b4c8; text-shadow: 1px 1px 4px rgba(0,0,0,0.5); margin-bottom: 20px; }
                .player-stats { display: flex; align-items: stretch; gap: 20px; margin-top: 20px; }
                .stat-item { display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 1.8em; }
                .stat-item span:first-child { font-weight: bold; font-size: 1.5em; }
                .stat-item span:last-child { font-size: 0.8em; color: #a9b4c8; }
                .stat-item-season { margin-left: 44px; padding-left: 44px; border-left: 1px solid rgba(255,255,255,0.18); }
                .stat-item-season span:first-child { color: #fecb00; }
                .badges { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 22px; }
                .pill { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16); border-radius: 999px; }
                .pill-value { font-size: 1.9em; font-weight: 700; line-height: 1; color: #ffffff; }
                .pill-tag { font-size: 1.0em; color: #8a94a8; letter-spacing: 0.05em; text-transform: uppercase; }
                .pill-star { color: #fecb00; font-size: 1.4em; line-height: 1; }
                .auction-text { font-size: 1.5em; margin-top: 20px; font-style: italic; white-space: pre-wrap; }
            </style>
            <div class="card">
                <img src="${player.photoUrl}" alt="${player.name}" class="player-image" />
                <div class="player-info">
                    <h1 class="player-name">${player.name}</h1>
                    <h2 class="player-club">
                        <img src="${player.clubLogo}" alt="${player.clubName} logo" class="club-logo" />
                        ${player.clubName}
                    </h2>
                    <p class="player-details">Positie: ${player.position}</p>
                    ${badgesHtml}
                    ${statsHtml}
                    <p class="auction-text">${player.auctionText || ''}</p>
                </div>
            </div>
        `;
    }, []);

    const updateAuctionWindow = useCallback((player, forceWifeView = false) => {
        const auctionWindow = window.open('', 'auction-window');
        if (auctionWindow && player) {
            auctionWindow.document.body.innerHTML = '';
            const stats = playerStats[player.id];
            const prevSeasonPoints = prevSeasonTotals[String(player.id)]?.totalPoints;
            // Vast opgeslagen bij de overgang naar de Veiling (zie AdminOverview).
            const scoutCount = player.scoutCount;
            const playerCardHtml = getPlayerCardHtml(player, stats, auction?.backgroundUrl, prevSeasonPoints, scoutCount);

            const addClickHandler = (win) => {
                if (decoupledControl) {
                    win.document.body.style.cursor = 'pointer';
                    win.document.body.onclick = () => {
                        if (win.opener && !win.opener.closed && typeof win.opener.showNextPlayer === 'function') {
                            win.opener.showNextPlayer();
                        }
                    };
                }
            };

            if (player.wifeImageUrl && forceWifeView) {
                 shownCardPlayerRef.current = null; // spelersvrouw in beeld — niet hertekenen
                 auctionWindow.document.body.innerHTML = `
                    <style>
                        body { margin: 0; background-color: #000; display: flex; justify-content: center; align-items: center; height: 100vh; cursor: pointer; }
                        img { max-height: 100%; max-width: 100%; }
                    </style>
                    <img src="${player.wifeImageUrl}" alt="Spelersvrouw" />
                `;
                addClickHandler(auctionWindow);
            } else {
                shownCardPlayerRef.current = player; // deze spelerskaart staat nu in beeld
                auctionWindow.document.body.innerHTML = playerCardHtml;
                addClickHandler(auctionWindow);
            }
        }
    }, [getPlayerCardHtml, decoupledControl, auction, playerStats, prevSeasonTotals]);

    // Herteken de getoonde spelerskaart zodra async data (spelerstats, vorig-seizoen-
    // totalen of scout-tellingen) binnenkomt, zodat badges niet ontbreken door een race
    // tussen het openen van de kaart en het laden van die data.
    useEffect(() => {
        const player = shownCardPlayerRef.current;
        if (player) {
            updateAuctionWindow(player, false);
        }
        // updateAuctionWindow bewust buiten de deps: we willen alleen hertekenen als de
        // onderliggende data verandert, niet bij elke veiling-update (voorkomt geflikker).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevSeasonTotals, playerStats]);

    const showNextDecoupledPlayer = useCallback(() => {
        const currentPlayerInList = decoupledPlayerList.find(p => p.id === decoupledPlayerId);
    
        if (isShowingWifeForDecoupled && currentPlayerInList) {
            setIsShowingWifeForDecoupled(false);
            updateAuctionWindow(currentPlayerInList, false);
            return;
        }
    
        const currentIndex = decoupledPlayerList.findIndex(p => p.id === decoupledPlayerId);
        const nextPlayerIndex = currentIndex + 1;
    
        if (nextPlayerIndex < decoupledPlayerList.length) {
            const nextPlayer = decoupledPlayerList[nextPlayerIndex];
            setDecoupledPlayerId(nextPlayer.id);
            if (nextPlayer.wifeImageUrl) {
                setIsShowingWifeForDecoupled(true);
                updateAuctionWindow(nextPlayer, true);
            } else {
                setIsShowingWifeForDecoupled(false);
                updateAuctionWindow(nextPlayer, false);
            }
        } else {
            setDecoupledPlayerId(null);
            const auctionWindow = window.open('', 'auction-window');
            if (auctionWindow) {
                auctionWindow.document.body.innerHTML = '<h1>Einde van de veiling</h1>';
                auctionWindow.document.body.style.cursor = 'default';
                auctionWindow.document.body.onclick = null;
            }
        }
    }, [decoupledPlayerList, decoupledPlayerId, isShowingWifeForDecoupled, updateAuctionWindow]);

    const handleShowCurrentDecoupledPlayer = useCallback(() => {
        if (decoupledControl && decoupledPlayerList.length > 0) {
            let playerToShow = decoupledPlayerList.find(p => p.id === decoupledPlayerId);
    
            if (!playerToShow) {
                playerToShow = decoupledPlayerList[0];
                if(playerToShow) {
                    setDecoupledPlayerId(playerToShow.id);
                    const showWife = !!playerToShow.wifeImageUrl;
                    setIsShowingWifeForDecoupled(showWife);
                    updateAuctionWindow(playerToShow, showWife);
                }
            } else {
                updateAuctionWindow(playerToShow, isShowingWifeForDecoupled);
            }
        }
    }, [decoupledControl, decoupledPlayerList, decoupledPlayerId, isShowingWifeForDecoupled, updateAuctionWindow]);

    useEffect(() => {
        if (decoupledControl) {
            window.showNextPlayer = showNextDecoupledPlayer;
        } else {
            window.showNextPlayer = null;
        }
        return () => {
            window.showNextPlayer = null;
        };
    }, [decoupledControl, showNextDecoupledPlayer]);

    const handleToggleAuctionState = async () => {
        try {
            const auctionRef = doc(db, 'auctions', auctionId);
            const newAuctionState = !auctionStarted;

            if (newAuctionState) {
                await updateDoc(auctionRef, { auctionStarted: true });
                window.open('', 'auction-window', 'width=800,height=600');
                updateAuctionWindow(currentPlayer);
            } else {
                await updateDoc(auctionRef, { auctionStarted: false });
                window.open('', 'auction-window')?.close();
            }
        } catch (err) {
            setError("Fout bij het wijzigen van de veilingstatus.");
            console.error(err);
        }
    };


    const handleRegisterBid = async () => {
        if (!bidder || !amount) {
            setValidationErrorMessage("Selecteer een bieder en voer een bedrag in.");
            setValidationErrorOpen(true);
            return;
        }

        if (!currentPlayer) {
            setValidationErrorMessage("Geen speler meer om te veilen.");
            setValidationErrorOpen(true);
            return;
        }

        const winningParticipant = participants.find(p => p.uid === bidder);
        if (!winningParticipant) {
            setValidationErrorMessage("Geselecteerde bieder niet gevonden.");
            setValidationErrorOpen(true);
            return;
        }

        const bidderTransfers = auctionTransfers.filter(t => t.to === bidder || t.from === bidder);
        const teamPlayerIds = new Set();
        bidderTransfers.sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0))
            .forEach(t => {
                if (t.to === bidder) teamPlayerIds.add(String(t.playerId));
                if (t.from === bidder) teamPlayerIds.delete(String(t.playerId));
            });

        const teamPlayers = Array.from(teamPlayerIds).map(id => allPlayersMap.get(id)).filter(Boolean);
        const validationError = validateTeamComposition(teamPlayers, currentPlayer);

        if (validationError) {
            setValidationErrorMessage(validationError);
            setValidationErrorOpen(true);
            return;
        }

        const bidAmount = parseInt(amount, 10);
        const currentTeamSize = teamPlayers.length;
        const playersNeededAfterThis = TEAM_RULES['Totaal'].max - (currentTeamSize + 1);
        const EXTRA_BUDGET_POST_AUCTION = 10;
        const reservedBudget = Math.max(0, playersNeededAfterThis - EXTRA_BUDGET_POST_AUCTION);
        const maxAllowedBid = winningParticipant.budget - reservedBudget;

        if (bidAmount > maxAllowedBid) {
            setValidationErrorMessage(`Bod van €${bidAmount} is te hoog. Na dit bod moet er nog €${reservedBudget} overblijven voor ${playersNeededAfterThis} speler(s). Maximaal bod is €${maxAllowedBid}.`);
            setValidationErrorOpen(true);
            return;
        }

        try {
            const batch = writeBatch(db);
            const participantRef = doc(db, 'auctions', auctionId, 'participants', bidder);
            batch.update(participantRef, {
                budget: winningParticipant.budget - bidAmount,
                transfersLeft: (winningParticipant.transfersLeft || 0) - 1
            });

            const teamRef = doc(db, 'auctions', auctionId, 'teams', bidder);
            batch.set(teamRef, { players: arrayUnion(currentPlayer.id) }, { merge: true });

            const transferRef = doc(collection(db, 'auctions', auctionId, 'auctionTransfers'));
            batch.set(transferRef, {
                playerId: currentPlayer.id,
                playerName: currentPlayer.name,
                photoUrl: currentPlayer.photoUrl || null,
                from: 'veiling',
                to: bidder,
                toEmail: winningParticipant.email,
                amount: bidAmount,
                timestamp: serverTimestamp(),
                type: 'auction'
            });

            const auctionPlayerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', currentPlayer.id);
            batch.update(auctionPlayerRef, { auctioned: 1, auctionedTimestamp: serverTimestamp() });

            await batch.commit();

            if (!decoupledControl) {
                if (currentPlayer.wifeImageUrl) {
                    setPlayerJustSold(currentPlayer);
                    updateAuctionWindow(currentPlayer, false); // Show player card
                } else {
                    const nextPlayer = enrichedPlayersToAuction[1];
                    if (nextPlayer) {
                        if (nextPlayer.wifeImageUrl) {
                            updateAuctionWindow(nextPlayer, true);
                        } else {
                            updateAuctionWindow(nextPlayer, false);
                        }
                    } else {
                        const auctionRef = doc(db, 'auctions', auctionId);
                        updateDoc(auctionRef, { auctionStarted: false });
                        window.open('', 'auction-window')?.close();
                    }
                }
            }

            setBidder('');
            setAmount('');
            setError(null);
        } catch (err) {
            setError("Fout bij het registreren van het bod.");
            console.error(err);
        }
    };

    const handleMoveToBlindPool = async () => {
        if (!currentPlayer) {
            setError("Geen speler om naar de blinde pool te verplaatsen.");
            return;
        }

        try {
            const batch = writeBatch(db);
            const auctionPlayerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', currentPlayer.id);
            batch.update(auctionPlayerRef, { auctioned: 1, auctionedTimestamp: serverTimestamp() });

            await batch.commit();

            if (!decoupledControl) {
                if (currentPlayer.wifeImageUrl) {
                    setPlayerJustSold(currentPlayer);
                    updateAuctionWindow(currentPlayer, false); // Show player card
                } else {
                    const nextPlayer = enrichedPlayersToAuction[1];
                    if (nextPlayer) {
                        if (nextPlayer.wifeImageUrl) {
                            updateAuctionWindow(nextPlayer, true);
                        } else {
                            updateAuctionWindow(nextPlayer, false);
                        }
                    } else {
                        const auctionRef = doc(db, 'auctions', auctionId);
                        updateDoc(auctionRef, { auctionStarted: false });
                        window.open('', 'auction-window')?.close();
                    }
                }
            }
            setError(null);
        } catch (err) {
            setError("Fout bij het verplaatsen van de speler naar de blinde pool.");
            console.error(err);
        }
    };

    const handleNextPlayer = () => {
        setPlayerJustSold(null);
        const nextPlayer = enrichedPlayersToAuction[0];
        if (nextPlayer) {
            if (nextPlayer.wifeImageUrl) {
                updateAuctionWindow(nextPlayer, true);
            } else {
                updateAuctionWindow(nextPlayer, false);
            }
        } else {
            const auctionRef = doc(db, 'auctions', auctionId);
            updateDoc(auctionRef, { auctionStarted: false });
            window.open('', 'auction-window')?.close();
        }
    };

    const handleClickOpen = (player) => {
        setSelectedPlayer(player);
        setAuctionText(player.auctionText || '');
        setShowWifeImage(!!player.wifeImageUrl);
        setWifeImageFile(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedPlayer(null);
        setAuctionText('');
        setShowWifeImage(false);
        setWifeImageFile(null);
    };

    const handleSave = async () => {
        if (!selectedPlayer) return;

        setIsUploading(true);
        let imageUrl = selectedPlayer.wifeImageUrl || '';

        try {
            if (wifeImageFile) {
                const storageRef = ref(storage, `auctions/${auctionId}/playerWives/${selectedPlayer.id}`);
                await uploadBytes(storageRef, wifeImageFile);
                imageUrl = await getDownloadURL(storageRef);
            }

            const dataToSave = {
                auctionText,
                wifeImageUrl: showWifeImage ? imageUrl : deleteField()
            };

            const playerDocRef = doc(db, 'auctions', auctionId, 'auctionPlayers', selectedPlayer.id);
            await updateDoc(playerDocRef, dataToSave);

            handleClose();
        } catch (err) {
            setError('Fout bij het opslaan.');
            console.error(err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleOpenConfirmDelete = (player) => {
        setPlayerToDelete(player);
        setConfirmDeleteOpen(true);
    };

    const handleCloseConfirmDelete = () => {
        setPlayerToDelete(null);
        setConfirmDeleteOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!playerToDelete || !auctionId) return;
        try {
            const playerDocRef = doc(db, 'auctions', auctionId, 'auctionPlayers', playerToDelete.id);
            await deleteDoc(playerDocRef);
        } catch (err) {
            setError("Fout bij het verwijderen van de speler.");
            console.error("Error deleting player:", err);
        } finally {
            handleCloseConfirmDelete();
        }
    };

    const handleOpenConfirmDeleteTransfer = (transfer) => {
        setTransferToDelete(transfer);
        setConfirmDeleteTransferOpen(true);
    };

    const handleCloseConfirmDeleteTransfer = () => {
        setTransferToDelete(null);
        setConfirmDeleteTransferOpen(false);
    };

    const handleConfirmDeleteTransfer = async () => {
        if (!transferToDelete || !auctionId) return;

        const winningParticipant = participants.find(p => p.uid === transferToDelete.to);

        if (!winningParticipant) {
            setError(`Kon de deelnemer (${transferToDelete.toEmail}) niet vinden om het budget terug te storten.`);
            console.error("Could not find participant to refund for transfer:", transferToDelete);
            handleCloseConfirmDeleteTransfer();
            return;
        }

        try {
            const batch = writeBatch(db);
            const transferRef = doc(db, 'auctions', auctionId, 'auctionTransfers', transferToDelete.id);
            batch.delete(transferRef);

            const playerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', String(transferToDelete.playerId));
            batch.update(playerRef, { auctioned: 0, auctionedTimestamp: deleteField() });

            const participantRef = doc(db, 'auctions', auctionId, 'participants', winningParticipant.uid);
            const newBudget = winningParticipant.budget + transferToDelete.amount;
            const newTransfersLeft = (winningParticipant.transfersLeft || 0) + 1;
            batch.update(participantRef, { budget: newBudget, transfersLeft: newTransfersLeft });

            await batch.commit();
        } catch (err) {
            setError("Fout bij het ongedaan maken van de transfer.");
            console.error("Error reverting transfer:", err);
        } finally {
            handleCloseConfirmDeleteTransfer();
        }
    };

    const handleOpenConfirmRevertBlindPool = (player) => {
        setPlayerToRevert(player);
        setConfirmRevertBlindPoolOpen(true);
    };

    const handleCloseConfirmRevertBlindPool = () => {
        setPlayerToRevert(null);
        setConfirmRevertBlindPoolOpen(false);
    };

    const handleConfirmRevertBlindPool = async () => {
        if (!playerToRevert || !auctionId) return;

        try {
            const playerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', playerToRevert.id);
            await updateDoc(playerRef, { auctioned: 0, auctionedTimestamp: deleteField() });
        } catch (err) {
            setError("Fout bij het terugdraaien van de blinde pool actie.");
            console.error("Error reverting blind pool player:", err);
        } finally {
            handleCloseConfirmRevertBlindPool();
        }
    };

    const handleEditClick = (transfer) => {
        setEditingTransferId(transfer.id);
        setEditingAmount(transfer.amount);
    };

    const handleSaveAmount = async () => {
        if (!editingTransferId) return;

        const newAmount = parseInt(editingAmount, 10);
        if (isNaN(newAmount) || newAmount < 0) {
            setEditingTransferId(null);
            return;
        }

        const transferRef = doc(db, 'auctions', auctionId, 'auctionTransfers', editingTransferId);

        try {
            await runTransaction(db, async (transaction) => {
                const transferDoc = await transaction.get(transferRef);
                if (!transferDoc.exists()) {
                    throw new Error("Transfer document not found!");
                }

                const transferData = transferDoc.data();
                const oldAmount = transferData.amount;
                const amountDifference = newAmount - oldAmount;

                if (amountDifference === 0) {
                    return; 
                }

                const participantId = transferData.to;
                const participantRef = doc(db, 'auctions', auctionId, 'participants', participantId);
                const participantDoc = await transaction.get(participantRef);

                if (!participantDoc.exists()) {
                    throw new Error("Participant document not found!");
                }

                const participantData = participantDoc.data();
                const newBudget = participantData.budget - amountDifference;

                if (newBudget < 0) {
                    throw new Error(`Budget ontoereikend voor deelnemer ${participantData.name || participantData.email}.`);
                }

                transaction.update(transferRef, { amount: newAmount });
                transaction.update(participantRef, { budget: newBudget });
            });

            setError(null);
        } catch (error) {
            console.error("Error updating transfer amount: ", error);
            setError(typeof error === 'string' ? error : "Fout bij het bijwerken van het bedrag.");
        } finally {
            setEditingTransferId(null);
        }
    };


    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!active || !over || active.id === over.id) {
            return;
        }

        const originalPlayersToAuction = playersForAuction.filter(p => !p.auctioned);
        const oldIndex = originalPlayersToAuction.findIndex((p) => p.id === active.id);
        const newIndex = originalPlayersToAuction.findIndex((p) => p.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const reorderedPlayersToAuction = arrayMove(originalPlayersToAuction, oldIndex, newIndex);
        const auctionedPlayers = playersForAuction.filter(p => p.auctioned);
        setPlayersForAuction([...reorderedPlayersToAuction, ...auctionedPlayers]);

        const batch = writeBatch(db);
        reorderedPlayersToAuction.forEach((player, index) => {
            const playerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', player.id);
            batch.update(playerRef, { auctionOrder: index });
        });
        auctionedPlayers.forEach((player, index) => {
            const playerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', player.id);
            batch.update(playerRef, { auctionOrder: reorderedPlayersToAuction.length + index });
        });

        try {
            await batch.commit();
            setError(null);
        } catch (err) {
            setError("Fout bij het opslaan van de nieuwe spelervolgorde.");
            console.error("Error updating player order:", err);
            setPlayersForAuction(playersForAuction);
        }
    };

    const handleExportPlayersToAuction = () => {
        const dataToExport = enrichedPlayersToAuction.map(p => ({
            Speler: p.name,
            Positie: p.position,
            Club: p.clubName,
            Veilingvolgorde: p.auctionOrder,
            Veilingtekst: p.auctionText || '',
            'Foto URL': p.photoUrl || '',
            'Spelersvrouw URL': p.wifeImageUrl || ''
        }));
        exportToExcel(dataToExport, 'Te_Veilen_Spelers');
    };

    const handleShufflePlayers = async () => {
        if (!auctionId || enrichedPlayersToAuction.length === 0) return;

        const shuffledPlayers = [...enrichedPlayersToAuction].sort(() => Math.random() - 0.5);

        const batch = writeBatch(db);
        shuffledPlayers.forEach((player, index) => {
            const playerRef = doc(db, 'auctions', auctionId, 'auctionPlayers', player.id);
            batch.update(playerRef, { auctionOrder: index });
        });

        try {
            await batch.commit();
            setError(null);
        } catch (err) {
            setError("Fout bij het husselen van de spelervolgorde.");
            console.error("Error shuffling player order:", err);
        } finally {
            setConfirmShuffleOpen(false);
        }
    };

    const handleExportAuctionedOffPlayers = () => {
        const dataToExport = auctionedOffPlayers.map(t => ({
            Speler: t.playerName,
            Naar: participantsMap.get(t.to) || t.toEmail,
            Bedrag: t.amount
        }));
        exportToExcel(dataToExport, 'Geveilde_Spelers');
    };

    const handleExportBlindPoolPlayers = () => {
        const dataToExport = blindPoolPlayers.map(p => ({
            Speler: p.name,
            Club: p.clubName,
            Positie: p.position
        }));
        exportToExcel(dataToExport, 'Blinde_Pool_Spelers');
    };

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }

    if (error && !validationErrorOpen) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Veiling Beheren - {auction?.name}</Typography>

            <Box sx={page.sectionHeader}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button variant="contained" size="large" onClick={handleToggleAuctionState}>
                        {auctionStarted ? 'Pauzeer Veiling' : 'Start Veiling'}
                    </Button>
                    {decoupledControl && auctionStarted && (
                        <Button variant="outlined" size="large" onClick={handleShowCurrentDecoupledPlayer}>
                            Spelerspagina tonen
                        </Button>
                    )}
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <FormControlLabel
                    control={<Switch checked={decoupledControl} onChange={handleToggleDecoupledControl} />}
                    label="Losgekoppelde bediening"
                />
            </Box>

            {auctionStarted && !playerJustSold && (
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h5" gutterBottom>Huidige Speler: {currentPlayer?.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Deelnemer</InputLabel>
                            <Select value={bidder} label="Deelnemer" onChange={(e) => setBidder(e.target.value)}>
                                {participants.map(p => (
                                    <MenuItem key={p.uid} value={p.uid}>{p.name || p.email}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Bedrag" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        
                        <Button variant="contained" color="primary" onClick={handleRegisterBid} disabled={!bidder || !amount}>Registreer Bod</Button>

                        <Button variant="outlined" color="secondary" onClick={handleMoveToBlindPool}>Naar blinde pool</Button>
                    </Box>
                </Paper>
            )}
            {auctionStarted && playerJustSold && (
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h5" gutterBottom>Speler Verkocht/Verplaatst</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {playerJustSold.name} is verwerkt. Klik op volgende speler om door te gaan.
                    </Typography>
                    <Button variant="contained" onClick={handleNextPlayer}>
                        Volgende Speler
                    </Button>
                </Paper>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" gutterBottom>Te Veilen Spelers ({enrichedPlayersToAuction.length})</Typography>
                        <Box>
                            <IconButton onClick={() => setConfirmShuffleOpen(true)} aria-label="Hussel de volgorde">
                                <ShuffleIcon />
                            </IconButton>
                            <IconButton onClick={handleExportPlayersToAuction} aria-label="Exporteer te veilen spelers">
                                <FileDownloadIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                        <SortableContext items={enrichedPlayersToAuction.map(p => p.id)} strategy={verticalListSortingStrategy}>
                            <TableContainer component={Paper}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '40px' }}></TableCell>
                                            <TableCell>Speler</TableCell>
                                            <TableCell>Positie</TableCell>
                                            <TableCell>Club</TableCell>
                                            <TableCell align="center">Tekst</TableCell>
                                            <TableCell align="right">Acties</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {enrichedPlayersToAuction.map((player) => (
                                            <SortablePlayerRow key={player.id} player={player} handleClickOpen={handleClickOpen} handleOpenConfirmDelete={handleOpenConfirmDelete} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </SortableContext>
                    </DndContext>
                </Box>
                <Box sx={{ width: { xs: '100%', md: '33%' } }}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" gutterBottom onClick={() => setAllAuctionedOpen(true)} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>Geveild ({auctionedOffPlayers.length})</Typography>
                        <IconButton onClick={handleExportAuctionedOffPlayers} aria-label="Exporteer geveilde spelers">
                            <FileDownloadIcon />
                        </IconButton>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Speler</TableCell>
                                    <TableCell>Naar</TableCell>
                                    <TableCell align="right">Bedrag</TableCell>
                                    <TableCell align="right">Acties</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auctionedOffPlayers.slice(0, 5).map((transfer) => (
                                    <TableRow key={transfer.id} hover>
                                        <TableCell>{transfer.playerName}</TableCell>
                                        <TableCell>{participantsMap.get(transfer.to) || transfer.toEmail}</TableCell>
                                        <TableCell align="right" onClick={() => handleEditClick(transfer)} sx={{ cursor: 'pointer' }}>
                                            {editingTransferId === transfer.id ? (
                                                <TextField
                                                    value={editingAmount}
                                                    onChange={(e) => setEditingAmount(e.target.value)}
                                                    onBlur={handleSaveAmount}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleSaveAmount();
                                                        } else if (e.key === 'Escape') {
                                                            setEditingTransferId(null);
                                                        }
                                                    }}
                                                    type="number"
                                                    size="small"
                                                    autoFocus
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ width: '80px' }}
                                                />
                                            ) : (
                                                `€${transfer.amount}`
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="maak transfer ongedaan"
                                                size="small"
                                                onClick={() => handleOpenConfirmDeleteTransfer(transfer)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
                        <Typography variant="h5" gutterBottom>Blinde Pool ({blindPoolPlayers.length})</Typography>
                        <IconButton onClick={handleExportBlindPoolPlayers} aria-label="Exporteer blinde pool spelers">
                            <FileDownloadIcon />
                        </IconButton>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Speler</TableCell>
                                    <TableCell align="right">Acties</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {blindPoolPlayers.slice(0, 5).map((player) => (
                                    <TableRow key={player.id} hover>
                                        <TableCell>{player.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="maak ongedaan"
                                                size="small"
                                                onClick={() => handleOpenConfirmRevertBlindPool(player)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>

            <Dialog open={allAuctionedOpen} onClose={() => setAllAuctionedOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Alle Geveilde Spelers</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Speler</TableCell>
                                    <TableCell>Naar</TableCell>
                                    <TableCell align="right">Bedrag</TableCell>
                                    <TableCell align="right">Acties</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auctionedOffPlayers.map((transfer) => (
                                    <TableRow key={transfer.id} hover>
                                        <TableCell>{transfer.playerName}</TableCell>
                                        <TableCell>{participantsMap.get(transfer.to) || transfer.toEmail}</TableCell>
                                        <TableCell align="right" onClick={() => handleEditClick(transfer)} sx={{ cursor: 'pointer' }}>
                                            {editingTransferId === transfer.id ? (
                                                <TextField
                                                    value={editingAmount}
                                                    onChange={(e) => setEditingAmount(e.target.value)}
                                                    onBlur={handleSaveAmount}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveAmount();
                                                        else if (e.key === 'Escape') setEditingTransferId(null);
                                                    }}
                                                    type="number"
                                                    size="small"
                                                    autoFocus
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ width: '80px' }}
                                                />
                                            ) : (
                                                `€${transfer.amount}`
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="maak transfer ongedaan"
                                                size="small"
                                                onClick={() => handleOpenConfirmDeleteTransfer(transfer)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAllAuctionedOpen(false)}>Sluiten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={validationErrorOpen} onClose={() => setValidationErrorOpen(false)}>
                <DialogTitle>Validatie Fout</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {validationErrorMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationErrorOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Veilingtekst voor {selectedPlayer?.name}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Voer hier de tekst in die tijdens de veiling voor deze speler wordt getoond.
                    </DialogContentText>
                    <TextField autoFocus margin="dense" label="Veilingtekst" type="text" fullWidth variant="outlined" multiline rows={4} value={auctionText} onChange={(e) => setAuctionText(e.target.value)} />
                    
                    <FormControlLabel
                        control={<Checkbox checked={showWifeImage} onChange={(e) => setShowWifeImage(e.target.checked)} />}
                        label="Spelersvrouw tonen"
                    />

                    {showWifeImage && (
                        <Box mt={2}>
                            <Button variant="contained" component="label">
                                Upload Afbeelding
                                <input type="file" hidden accept="image/*" onChange={(e) => setWifeImageFile(e.target.files[0])} />
                            </Button>
                            {wifeImageFile && <Typography variant="body2" sx={{ display: 'inline', ml: 2 }}>{wifeImageFile.name}</Typography>}
                            {(selectedPlayer?.wifeImageUrl && !wifeImageFile) && (
                                <Box mt={1}>
                                    <Typography variant="caption">Huidige afbeelding:</Typography>
                                    <Avatar src={selectedPlayer.wifeImageUrl} sx={{ width: 60, height: 60, mt: 1 }} />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Annuleren</Button>
                    <Button onClick={handleSave} disabled={isUploading}>
                        {isUploading ? <CircularProgress size={24} /> : 'Opslaan'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteOpen} onClose={handleCloseConfirmDelete}>
                <DialogTitle>Speler Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je <strong>{playerToDelete?.name}</strong> wilt verwijderen uit de lijst met te veilen spelers? Deze actie kan niet ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDelete}>Annuleren</Button>
                    <Button onClick={handleConfirmDelete} color="error">Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteTransferOpen} onClose={handleCloseConfirmDeleteTransfer}>
                <DialogTitle>Transfer Ongedaan Maken?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de transfer van <strong>{transferToDelete?.playerName}</strong> naar <strong>{participantsMap.get(transferToDelete?.to) || transferToDelete?.toEmail}</strong> ongedaan wilt maken? De speler komt terug op de lijst met te veilen spelers.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteTransfer}>Annuleren</Button>
                    <Button onClick={handleConfirmDeleteTransfer} color="error">Bevestigen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmShuffleOpen} onClose={() => setConfirmShuffleOpen(false)}>
                <DialogTitle>Volgorde Husselen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de volgorde van de te veilen spelers wilt husselen? Dit kan niet ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmShuffleOpen(false)}>Annuleren</Button>
                    <Button onClick={handleShufflePlayers} color="primary">Husselen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmRevertBlindPoolOpen} onClose={handleCloseConfirmRevertBlindPool}>
                <DialogTitle>Actie Terugdraaien?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je <strong>{playerToRevert?.name}</strong> wilt terughalen uit de blinde pool? De speler komt terug op de lijst met te veilen spelers.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmRevertBlindPool}>Annuleren</Button>
                    <Button onClick={handleConfirmRevertBlindPool} color="error">Bevestigen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminAuction;